import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button, Progress, Spin, Typography } from 'antd';
import { ChevronDown, Repeat, Circle } from 'lucide-react';
import { motion } from 'framer-motion';
import type { AssessmentResponse, AssessmentScoreTrend, ItemScoreTrend } from '@evaltick/shared';
import { AnswerBreakdown } from '@/components/Interview/AnswerBreakdown';
import { ScoreTrendCard } from '@/components/Interview/ScoreTrendCard';
import { Sparkline } from '@/components/Sparkline';
import { useBreakpoint, useCountUp } from '@/hooks';
import { formatScoreDelta, scoreToLabel, toneClass } from '@/utils';
import styles from './EvalChart.module.scss';

const { Text } = Typography;

const OVERALL_RING_SIZE = 168;
const OVERALL_RING_STROKE = 8;
const WORK_ON_NEXT = 'Work on next';
const FOCUS_PROMPT = 'Your lowest-scoring answer is the quickest way to lift this score — retake it next.';
const ROW_REVEAL_STEP_MS = 70;
const COUNT_UP_SECONDS = 0.9;
const RING_GRADIENT_ID = 'results-overall-ring-gradient';
const MAX_STAGGERED_ROWS = 6;

interface EvalChartProps {
  responses: AssessmentResponse[];
  /** Overall score for this sitting — already computed server-side (Assessment.totalScore). */
  totalScore: number | null;
  /** Delta vs. the previous attempt at the same questions — null if none exists. */
  totalScoreDelta?: number | null;
  /** Assessment.summaryNarrative — null while not yet generated. */
  summaryNarrative?: string | null;
  /** True while the summary is being generated server-side. */
  summaryLoading?: boolean;
  /** Up to 10 sittings of score history for this template — null/absent hides the trend card. */
  scoreTrend?: AssessmentScoreTrend | null;
}

const rowElementId = (index: number) => `results-question-${index + 1}`;

const scoreBarWidth = (score: number) => ({ '--score-bar-width': `${score}%` }) as React.CSSProperties;

const weakestIndexOf = (responses: AssessmentResponse[]): number => {
  let weakest = -1;
  responses.forEach((response, index) => {
    if (response.overallScore == null) return;
    if (weakest < 0 || response.overallScore < (responses[weakest].overallScore ?? 0)) weakest = index;
  });
  return weakest;
};

interface AnnotationsProps {
  response: AssessmentResponse;
}

const Annotations: React.FC<AnnotationsProps> = ({ response }) => {
  const comparable = !response.isCarriedOver && !response.sameAsPreviousAnswer;
  const deltaText = comparable && response.scoreDelta != null ? formatScoreDelta(response.scoreDelta) : null;
  const plainText = response.isCarriedOver
    ? null
    : response.sameAsPreviousAnswer
      ? 'Same as last attempt'
      : response.scoreDelta == null && response.previousOverallScore == null
        ? 'First attempt'
        : null;
  const signTone =
    response.scoreDelta == null || response.scoreDelta === 0
      ? ''
      : response.scoreDelta > 0
        ? styles.deltaSignUp
        : styles.deltaSignDown;

  return (
    <span className={styles.annotations}>
      {response.isCarriedOver ? (
        <span className={styles.chip}>
          <Circle aria-hidden="true" />
          From previous session
        </span>
      ) : (
        response.isDifferentQuestion && (
          <span className={styles.chip}>
            <Repeat aria-hidden="true" />
            vs. a different question, same competency
          </span>
        )
      )}
      {deltaText && (
        <span className={styles.chip}>
          <span>
            <span className={`${styles.deltaSign} ${signTone}`}>{deltaText.charAt(0)}</span>
            {deltaText.slice(1)}
          </span>
        </span>
      )}
      {plainText && (
        <Text type="secondary" className={styles.annotationPlain}>
          {plainText}
        </Text>
      )}
    </span>
  );
};

const RowMeta: React.FC<{ response: AssessmentResponse }> = ({ response }) => {
  const score = response.overallScore ?? 0;

  return (
    <span className={styles.rowMeta}>
      <Annotations response={response} />
      <span className={`${styles.bandPill} ${toneClass(styles, response.overallScore)}`}>
        {score > 0 ? scoreToLabel(score) : 'Not scored'}
      </span>
    </span>
  );
};

interface RowHeadProps {
  index: number;
  response: AssessmentResponse;
  showMeta: boolean;
}

const RowHead: React.FC<RowHeadProps> = ({ index, response, showMeta }) => {
  const score = response.overallScore ?? 0;
  const scored = score > 0;
  const revealedScore = useCountUp(score, {
    delayMs: Math.min(index, MAX_STAGGERED_ROWS) * ROW_REVEAL_STEP_MS,
  });

  return (
    <span className={styles.head}>
      <span className={styles.questionNumber}>Q{index + 1}</span>
      <span className={styles.questionText} title={response.questionText ?? undefined}>
        {response.questionText}
      </span>
      {scored && (
        <span className={styles.score}>
          <span className={styles.scoreValue}>
            {Math.round(revealedScore)}
            <small>%</small>
          </span>
          <span className={`${styles.scoreBar} ${toneClass(styles, response.overallScore)}`}>
            <span className={styles.scoreBarFill} style={scoreBarWidth(revealedScore)} />
          </span>
        </span>
      )}
      {showMeta && <RowMeta response={response} />}
    </span>
  );
};

interface QuestionRowProps {
  index: number;
  response: AssessmentResponse;
  itemTrend: ItemScoreTrend | null;
  defaultOpen: boolean;
  isFocus: boolean;
}

const QuestionRow: React.FC<QuestionRowProps> = ({ index, response, itemTrend, defaultOpen, isFocus }) => {
  const hasDetail = (response.dimensionScores?.length ?? 0) > 0;
  const { isMobile } = useBreakpoint();
  const metaInDetail = isMobile && hasDetail;
  const [expanded, setExpanded] = useState(defaultOpen && hasDetail);
  const [mounted, setMounted] = useState(defaultOpen && hasDetail);

  return (
    <motion.article
      id={rowElementId(index)}
      className={`${styles.row} ${isFocus ? styles.rowFocus : ''}`}
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3, delay: Math.min(index, 6) * 0.05, ease: 'easeOut' }}
    >
      {isFocus && <span className={styles.focusTag}>{WORK_ON_NEXT}</span>}

      {hasDetail ? (
        <>
          <button
            type="button"
            className={styles.rowButton}
            aria-expanded={expanded}
            aria-controls={`${rowElementId(index)}-detail`}
            onClick={() => {
              setMounted(true);
              setExpanded((open) => !open);
            }}
          >
            <RowHead index={index} response={response} showMeta={!metaInDetail} />
            <span className={styles.rowChevron}>
              <ChevronDown aria-hidden="true" />
            </span>
          </button>

          {mounted && (
            <motion.div
              id={`${rowElementId(index)}-detail`}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              className={styles.detailWrapper}
            >
              <div className={styles.detail}>
                {metaInDetail && <RowMeta response={response} />}
                {response.rationale && <p className={styles.rationale}>{response.rationale}</p>}
                <AnswerBreakdown
                  itemTrend={itemTrend}
                  dimensionScores={response.dimensionScores}
                  competencyId={response.competencyId}
                  questionType={response.questionType}
                  structureScore={response.structureScore}
                  structureRationale={response.structureRationale}
                  structureComponentScores={response.structureComponentScores}
                />
              </div>
            </motion.div>
          )}
        </>
      ) : (
        <div className={styles.rowStatic}>
          <RowHead index={index} response={response} showMeta />
          {response.rationale && <p className={styles.rationale}>{response.rationale}</p>}
        </div>
      )}
    </motion.article>
  );
};

interface SessionSummaryProps {
  narrative: string | null | undefined;
  loading: boolean;
  focusIndex: number;
}

const SessionSummary: React.FC<SessionSummaryProps> = ({ narrative, loading, focusIndex }) => {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element || loading) return;
    const measure = () => setClamped(element.scrollHeight > element.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [narrative, loading, expanded]);

  return (
    <div className={styles.summary}>
      <h3 className={styles.summaryTitle}>Session Summary</h3>

      {loading ? (
        <div className={styles.summaryLoading}>
          <Spin size="small" />
          <Text type="secondary">Generating your session summary…</Text>
        </div>
      ) : (
        <>
          <p
            ref={textRef}
            className={`${styles.summaryNarrative} ${expanded ? '' : styles.summaryClamped}`}
          >
            {narrative}
          </p>
          {(clamped || expanded) && (
            <Button type="link" className={styles.summaryMore} onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'Show less' : 'Read more'}
            </Button>
          )}
        </>
      )}

      {focusIndex >= 0 && (
        <p className={styles.focusPrompt}>{FOCUS_PROMPT}</p>
      )}
      {focusIndex >= 0 && (
        <a
          className={styles.focusLink}
          href={`#${rowElementId(focusIndex)}`}
          aria-label={`${WORK_ON_NEXT}: question ${focusIndex + 1}`}
        >
          {WORK_ON_NEXT}
        </a>
      )}
    </div>
  );
};

export const EvalChart: React.FC<EvalChartProps> = ({
  responses,
  totalScore,
  totalScoreDelta,
  summaryNarrative,
  summaryLoading,
  scoreTrend,
}) => {
  const overallPercent = totalScore != null ? Math.round(totalScore) : 0;
  const revealedOverall = Math.round(useCountUp(overallPercent, { durationSeconds: COUNT_UP_SECONDS }));
  const overallLabel = totalScore != null ? scoreToLabel(totalScore) : '—';
  const overallDeltaText = formatScoreDelta(totalScoreDelta);
  const showSummary = summaryLoading || !!summaryNarrative;

  const weakestIndex = weakestIndexOf(responses);
  const focusIndex = responses.length > 1 ? weakestIndex : -1;
  const overallSignTone =
    totalScoreDelta == null || totalScoreDelta === 0
      ? ''
      : totalScoreDelta > 0
        ? styles.deltaSignUp
        : styles.deltaSignDown;
  const deltaHistory = scoreTrend?.overall.map((attempt) => attempt.totalScore ?? 0);

  return (
    <div className={styles.container}>
      <section className={styles.scoreCard}>
        <div className={styles.overall}>
          <motion.div
            className={`${styles.ring} ${toneClass(styles, overallPercent)}`}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          >
            <svg className={styles.ringGradient} aria-hidden="true" focusable="false">
              <defs>
                <linearGradient id={RING_GRADIENT_ID} x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="var(--tone-half)" />
                  <stop offset="100%" stopColor="var(--tone)" />
                </linearGradient>
              </defs>
            </svg>
            <Progress
              type="circle"
              percent={revealedOverall}
              size={OVERALL_RING_SIZE}
              strokeColor={`url(#${RING_GRADIENT_ID})`}
              strokeWidth={OVERALL_RING_STROKE}
              format={() => (
                <span className={styles.ringLabel}>
                  <span className={styles.ringPercent}>
                    {revealedOverall}
                    <span className={styles.ringPercentSymbol}>%</span>
                  </span>
                  <span className={styles.ringBand}>{overallLabel}</span>
                </span>
              )}
            />
          </motion.div>
          <div className={styles.overallInfo}>
            <h3 className={styles.overallTitle}>Overall Score</h3>
            <Text type="secondary" className={styles.overallSub}>
              Based on {responses.length} question{responses.length === 1 ? '' : 's'}
            </Text>
            {overallDeltaText && (
              <span className={`${styles.chip} ${styles.chipLarge}`}>
                {deltaHistory && deltaHistory.length > 1 && <Sparkline values={deltaHistory} />}
                <span>
                  <span className={`${styles.deltaSign} ${overallSignTone}`}>
                    {overallDeltaText.charAt(0)}
                  </span>
                  {overallDeltaText.slice(1)}
                </span>
              </span>
            )}
          </div>
        </div>

        {showSummary && (
          <SessionSummary
            narrative={summaryNarrative}
            loading={!!summaryLoading}
            focusIndex={focusIndex}
          />
        )}
      </section>

      <ScoreTrendCard scoreTrend={scoreTrend} />

      <div className={styles.rows}>
        {responses.map((response, index) => (
          <QuestionRow
            key={response.id}
            index={index}
            response={response}
            itemTrend={
              scoreTrend?.items.find((i) => i.interviewTemplateItemId === response.interviewTemplateItemId) ?? null
            }
            defaultOpen={index === weakestIndex}
            isFocus={index === focusIndex}
          />
        ))}
      </div>
    </div>
  );
};
